import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { countryNameFor } from '../../util/custom/countryNames';
import NamedLink from '../NamedLink/NamedLink';
import IconAlert from '../IconAlert/IconAlert';
import { SecondaryButtonInline } from '../Button/Button';
import {
  AVAILABLE,
  PENDING,
  SOLD_OUT,
  NOT_FOR_SALE,
  INSUFFICIENT_STOCK,
  NOT_SHIPPABLE,
  SHIPPING_INCOMPLETE,
} from '../../util/custom/cartAvailability';

import css from './CartItemRow.module.css';

const { Money } = sdkTypes;

/**
 * The reason a row cannot be bought, in the buyer's words.
 *
 * D-13: always the specific reason. "Unavailable" alone sends a buyer looking for the
 * wrong problem — they cannot tell a sold item from one that will not ship to them, and
 * only one of those is worth changing their address for.
 */
const REASON_KEYS = {
  [SOLD_OUT]: 'CartItemRow.reasonSoldOut',
  [NOT_FOR_SALE]: 'CartItemRow.reasonNotForSale',
  [INSUFFICIENT_STOCK]: 'CartItemRow.reasonInsufficientStock',
  [NOT_SHIPPABLE]: 'CartItemRow.reasonNotShippable',
  [SHIPPING_INCOMPLETE]: 'CartItemRow.reasonShippingIncomplete',
};

/**
 * A link to the listing, or plain text when there is nothing to link to. A sold or deleted
 * listing still has a page, but sending a buyer to a dead end from a row that already says
 * "no longer available" adds nothing.
 */
const ListingLinkMaybe = ({ listingId, title, isUnavailable, children }) =>
  listingId && title && !isUnavailable ? (
    <NamedLink
      className={css.listingLink}
      name="ListingPage"
      params={{ id: listingId, slug: createSlug(title) }}
    >
      {children}
    </NamedLink>
  ) : (
    <>{children}</>
  );

/**
 * CL-CART-01 — one cart row (requirement 4, AC-9).
 *
 * The row stays visible when the item cannot be bought (D-12): tinted, marked, with the
 * reason, and excluded from the totals. Removing it silently is how a buyer discovers a
 * missing item after entering card details.
 *
 * Two separate quantity decisions meet here, and they are not the same question:
 *
 *   - `item.showsQuantityControl` — may THIS row's quantity change? Decided per row in
 *     annotateCartItem, from the listing's own stockType and stock.
 *   - `showsQuantityColumn` — does the PANEL have a quantity column at all? Decided by
 *     CartSellerGroup, because a column is shared by every row under it.
 *
 * A panel can therefore have the column while a given row inside it cannot move. That row
 * states its quantity instead of leaving the cell empty — an empty cell would shift every
 * later cell one column left, since grid auto-placement fills in DOM order.
 */
const CartItemRow = props => {
  const {
    className,
    rootClassName,
    item,
    showsQuantityColumn = false,
    panelOriginCountry = null,
    onSetQuantity,
    onRemove,
    // CL-NEG-01 requirement 13 — a locked seller group is read-only: no remove button. An empty
    // placeholder cell renders in its place rather than nothing at all, because the column
    // headers always render one trailing cell for the action column (CartSellerGroup.js) — grid
    // auto-placement fills cells in DOM order, so a row with fewer cells than the header shifts
    // every later cell one column left.
    hideRemove = false,
  } = props;
  const intl = useIntl();

  const {
    listingId,
    title,
    quantity,
    availableStock,
    status,
    unitPriceInSubunits,
    currency,
    lineTotalInSubunits,
    imageSnapshotUrl,
    shipping,
    isShippingFinal,
    maxQuantity,
    showsQuantityControl,
    priceChanged,
    previousPriceInSubunits,
    handlingTimeDays,
  } = item;

  // PENDING is not a fault, so it is not treated as one: no tint, no alert, no reason. The
  // row simply paints from its snapshot until the listing arrives — and if the fetch failed,
  // the page says so once above the panels rather than every row claiming to be gone.
  const isUnavailable = status !== AVAILABLE && status !== PENDING;

  const price = (amount, cur) =>
    Number.isInteger(amount) && cur ? formatMoney(intl, new Money(amount, cur)) : null;

  // With a quantity column the money cell is the line total, and the unit price is stated
  // under the title. Without one, quantity is always one, so the two figures are the same
  // number — printing both is the duplication this layout exists to remove.
  const moneyInSubunits = showsQuantityColumn ? lineTotalInSubunits : unitPriceInSubunits;

  const setQuantity = next => onSetQuantity({ listingId, quantity: next });

  // The panel header already names the origin when every row shares one, so a row repeats it
  // only when it differs — which is what CL-STOCK-01's several-locations-per-seller makes
  // possible. Saying "Ships from Denmark" on every row under a header that just said it is the
  // repetition this layout removed.
  const rowOrigin = shipping?.originCountry || null;
  const originName =
    rowOrigin && rowOrigin !== panelOriginCountry ? countryNameFor(rowOrigin, intl.locale) : null;
  const dispatchDays = Number.isInteger(handlingTimeDays) ? handlingTimeDays : null;

  // Nothing about buying it, on a row that cannot be bought. A sold-out item promising
  // "dispatch in 2 business days" reads as though it is fine, and "Price updated" invites the
  // buyer to consider a price they can never pay. D-12/D-13 make the REASON the row's
  // message; everything else here is noise competing with it.
  const showsBuyingDetail = !isUnavailable;

  return (
    <li
      className={classNames(rootClassName || css.root, className, {
        [css.narrow]: !showsQuantityColumn,
        [css.blocked]: isUnavailable,
      })}
    >
      {/* Both the image and the title link back to the listing. A cart you cannot click
          out of forces the buyer to search for the item again to check anything about it.
          A row whose listing is gone has nothing to link to, so it stays plain text. */}
      <ListingLinkMaybe listingId={listingId} title={title} isUnavailable={isUnavailable}>
        {imageSnapshotUrl ? (
          <img className={css.image} src={imageSnapshotUrl} alt={title || ''} />
        ) : (
          <div className={css.imagePlaceholder} />
        )}
      </ListingLinkMaybe>

      <div className={css.details}>
        <p className={css.title}>
          <ListingLinkMaybe listingId={listingId} title={title} isUnavailable={isUnavailable}>
            {title}
          </ListingLinkMaybe>
        </p>

        {/* Only where a quantity can multiply it. Otherwise it repeats the money cell. */}
        {showsQuantityColumn && unitPriceInSubunits != null ? (
          <p className={css.unitPrice}>
            <FormattedMessage
              id="CartItemRow.unitPrice"
              values={{ price: price(unitPriceInSubunits, currency) }}
            />
          </p>
        ) : null}

        {/* E4: the live price differs from the one stored when this was added. The buyer is
            charged the live figure shown above either way — this only stops the change
            passing unnoticed. */}
        {priceChanged && showsBuyingDetail ? (
          <p className={css.priceNote}>
            <FormattedMessage
              id="CartItemRow.priceWas"
              values={{
                previous: (
                  <span className={css.struck}>{price(previousPriceInSubunits, currency)}</span>
                ),
                current: price(unitPriceInSubunits, currency),
              }}
            />
          </p>
        ) : null}

        {/* AC-9's shipping information, per row: where it posts from and how soon. Only the
            parts that are known, and only the origin the header did not already state. */}
        {showsBuyingDetail && (originName || dispatchDays != null) ? (
          <p className={css.shipping}>
            {originName && dispatchDays != null ? (
              <FormattedMessage
                id="CartItemRow.originAndDispatch"
                values={{ country: originName, days: dispatchDays }}
              />
            ) : originName ? (
              <FormattedMessage id="CartItemRow.origin" values={{ country: originName }} />
            ) : (
              <FormattedMessage id="CartItemRow.dispatch" values={{ days: dispatchDays }} />
            )}
          </p>
        ) : null}

        {/* The shipping FEE is separate, and only shown when it is genuinely final and
            specific to this item (AC-18). With no address it repeated the same sentence on
            every row, so the group's summary says it once instead. */}
        {showsBuyingDetail && isShippingFinal && shipping?.amountInSubunits != null ? (
          <p className={css.shipping}>
            <FormattedMessage
              id="CartItemRow.shippingAmount"
              values={{ amount: price(shipping.amountInSubunits, currency) }}
            />
          </p>
        ) : null}

        {isUnavailable ? (
          <p className={css.reason}>
            <IconAlert className={css.reasonIcon} />
            <FormattedMessage
              id={REASON_KEYS[status] || 'CartItemRow.reasonNotForSale'}
              values={{ count: availableStock ?? 0 }}
            />
          </p>
        ) : null}
      </div>

      {/* AC-9: a control ONLY where the quantity can actually change. A stepper pinned at 1
          is noise, so a one-of-a-kind listing — and a multi-quantity one down to its last
          unit — gets none. See showsQuantityControl in cartAvailability.js. */}
      {showsQuantityControl ? (
        <div className={css.stepper}>
          <button
            className={css.stepButton}
            type="button"
            disabled={quantity <= 1}
            aria-label={intl.formatMessage({ id: 'CartItemRow.decreaseQuantity' })}
            onClick={() => setQuantity(quantity - 1)}
          >
            &minus;
          </button>
          <input
            className={css.quantity}
            type="number"
            min="1"
            max={maxQuantity ?? undefined}
            value={quantity}
            aria-label={intl.formatMessage({ id: 'CartItemRow.quantityLabel' })}
            onChange={e => setQuantity(Number(e.target.value))}
          />
          <button
            className={css.stepButton}
            type="button"
            disabled={maxQuantity != null && quantity >= maxQuantity}
            aria-label={intl.formatMessage({ id: 'CartItemRow.increaseQuantity' })}
            onClick={() => setQuantity(quantity + 1)}
          >
            +
          </button>
        </div>
      ) : showsQuantityColumn ? (
        <p className={css.quantityFixed}>{quantity}</p>
      ) : null}

      <p className={classNames(css.money, { [css.moneyExcluded]: isUnavailable })}>
        {price(moneyInSubunits, currency)}
      </p>

      {/* A real outlined button, matching the row actions on the locations page rather
          than a bare text link — the same decision and the same classes. */}
      {hideRemove ? (
        <span className={css.remove} aria-hidden="true" />
      ) : (
        <SecondaryButtonInline className={css.remove} onClick={() => onRemove({ listingId })}>
          <FormattedMessage id="CartItemRow.remove" />
        </SecondaryButtonInline>
      )}
    </li>
  );
};

export default CartItemRow;
